/* eslint-disable react/no-unescaped-entities */
/**
 * /fishing-tours/:slug — operator detail page with the all-important
 * lead-capture form.  Posts to /api/fishing-tours/:slug/enquire which
 * fires:
 *   • a styled email to the operator with the lead details + claim CTA
 *   • a confirmation email to the customer
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, MapPin, Anchor, Calendar, Users, Fish,
         Globe, CheckCircle2, Loader2, Send } from "lucide-react";
import TackleLogo from "../components/tackle/TackleLogo";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const api = axios.create({ baseURL: API });

const fmtAud = (n) => n ? `$${n.toLocaleString("en-AU")}` : "POA";

export default function FishingTourDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [tour, setTour] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  // form
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [party, setParty] = useState(2);
  const [date, setDate] = useState("");
  const [species, setSpecies] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    api.get(`/api/fishing-tours/${slug}`).then(r => {
      setTour(r.data); setLoading(false);
      setSpecies((r.data.target_species_list || [])[0] || "");
    }).catch(e => {
      setError(e?.response?.data?.detail || "Tour not found");
      setLoading(false);
    });
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError("");
    try {
      await api.post(`/api/fishing-tours/${slug}/enquire`, {
        name: name.trim(), email: email.trim(), phone: phone || null,
        party_size: parseInt(party, 10) || 1,
        trip_date: date || null, species: species || null,
        message: message || null,
      });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={s.root}>
        <div style={s.loading}><Loader2 size={28} className="spin" /> Loading…</div>
        <style>{spin}</style>
      </div>
    );
  }
  if (!tour) {
    return (
      <div style={s.root}>
        <div style={s.error}>{error || "Tour not found"}</div>
      </div>
    );
  }

  return (
    <div style={s.root} data-testid="tour-detail-root">
      <header style={s.header}>
        <a href="/fishing-tours" style={s.back} data-testid="tour-back">
          <ArrowLeft size={16} /> All charters
        </a>
        <a href="/tackle" style={s.brand}>
          <TackleLogo size={28} />
          <span style={s.brandText}>Tackle<span style={{ color: "#fbbf24" }}>.</span>au</span>
        </a>
      </header>

      <main style={s.main}>
        <section style={s.heroWrap}>
          <div style={{ ...s.hero, backgroundImage: `url("${tour.image || ''}")` }}>
            {tour.claimed_at
              ? <span style={s.claimed}>✓ Claimed operator</span>
              : <span style={s.unclaimed}>Temporary listing</span>}
            {tour.featured && <span style={s.featured}>★ Featured</span>}
          </div>
        </section>

        <section style={s.content}>
          <div style={s.left}>
            <h1 style={s.title} data-testid="tour-name">{tour.name}</h1>
            <div style={s.region}>
              <MapPin size={14} /> {tour.region}, {tour.country}
              {" "}
              <span style={s.dot}>•</span>
              {" "}
              <Anchor size={12} /> {tour.vessel || "Vessel TBC"}
            </div>

            <div style={s.factGrid}>
              <Fact icon={Fish} label="Target species" value={tour.target_species} />
              <Fact icon={Calendar} label="Season" value={tour.season} />
              <Fact icon={Users} label="Trip style" value={tour.trip_length} />
              <Fact icon={Globe} label="Website"
                     value={tour.website
                       ? <a href={tour.website} target="_blank" rel="noreferrer">
                           {tour.website.replace(/^https?:\/\//, "")}
                         </a>
                       : "—"} />
            </div>

            <div style={s.section}>
              <h3 style={s.h3}>About the operator</h3>
              <p style={s.body}>{tour.description}</p>
              {tour.specialty && (
                <div style={s.specialty}>
                  <Anchor size={12} /> Speciality: {tour.specialty}
                </div>
              )}
            </div>

            {!tour.claimed_at && (
              <div style={s.notice} data-testid="tour-temporary-notice">
                <strong>Heads up — this is a temporary directory listing.</strong>
                {" "}We've added this operator from public sources to fast-track
                introductions. Enquiries are still forwarded to them
                immediately. They'll be invited to claim and verify the
                listing when your enquiry lands.
              </div>
            )}
          </div>

          <aside style={s.formSide} data-testid="tour-enquiry-card">
            {done ? (
              <div style={s.doneCard} data-testid="tour-enquiry-done">
                <div style={s.doneBadge}><CheckCircle2 size={40} /></div>
                <h2 style={s.doneTitle}>Enquiry sent 🎣</h2>
                <p style={s.body}>
                  We've forwarded your details to <strong>{tour.name}</strong>
                  {" "}and emailed you a confirmation. Most operators reply
                  within 12 hours during their operating season.
                </p>
                <button
                  onClick={() => navigate("/fishing-tours")}
                  style={s.submitBtn}
                  data-testid="tour-enquiry-back"
                >
                  Browse more charters
                </button>
              </div>
            ) : (
              <form onSubmit={submit} style={s.form}>
                <div style={s.price}>
                  {tour.price_from_aud ? (
                    <>From <strong>{fmtAud(tour.price_from_aud)}</strong>
                      {" "}<span style={s.priceSub}>{tour.trip_length}</span></>
                  ) : "Price on application"}
                </div>
                <h3 style={s.formTitle}>Enquire direct</h3>
                <p style={{ ...s.body, fontSize: 13 }}>
                  No booking fees. The operator quotes you directly by email.
                </p>

                <Field label="Your name">
                  <input required value={name} onChange={e => setName(e.target.value)}
                         style={s.input} data-testid="enq-name" />
                </Field>
                <Field label="Email">
                  <input required type="email" value={email}
                         onChange={e => setEmail(e.target.value)}
                         style={s.input} data-testid="enq-email" />
                </Field>
                <Field label="Phone (optional)">
                  <input type="tel" value={phone} onChange={e => setPhone(e.target.value)}
                         style={s.input} data-testid="enq-phone" />
                </Field>
                <div style={s.row2}>
                  <Field label="Party size">
                    <input required type="number" min="1" max="40"
                           value={party} onChange={e => setParty(e.target.value)}
                           style={s.input} data-testid="enq-party" />
                  </Field>
                  <Field label="Preferred date">
                    <input type="date" value={date}
                           onChange={e => setDate(e.target.value)}
                           style={s.input} data-testid="enq-date" />
                  </Field>
                </div>
                {tour.target_species_list?.length > 0 && (
                  <Field label="Target species">
                    <select value={species} onChange={e => setSpecies(e.target.value)}
                            style={s.input} data-testid="enq-species">
                      <option value="">Any / operator's pick</option>
                      {tour.target_species_list.map(sp => (
                        <option key={sp} value={sp}>{sp}</option>
                      ))}
                    </select>
                  </Field>
                )}
                <Field label="Message (optional)">
                  <textarea rows={3} value={message}
                            onChange={e => setMessage(e.target.value)}
                            style={{ ...s.input, resize: "vertical" }}
                            data-testid="enq-message" />
                </Field>
                {error && <div style={s.errBody}>{error}</div>}
                <button
                  type="submit"
                  disabled={submitting}
                  style={{ ...s.submitBtn, opacity: submitting ? 0.5 : 1 }}
                  data-testid="enq-submit"
                >
                  {submitting
                    ? <><Loader2 size={16} className="spin" /> Sending…</>
                    : <><Send size={16} /> Send enquiry</>}
                </button>
                <p style={s.subtle}>
                  We forward your enquiry to the operator and copy you in.
                  Hustle Directory doesn't charge a booking fee.
                </p>
              </form>
            )}
          </aside>
        </section>
      </main>
      <style>{spin}</style>
    </div>
  );
}

function Fact({ icon: Icon, label, value }) {
  return (
    <div style={s.fact}>
      <Icon size={14} color="#1aa3c7" />
      <span style={s.factLabel}>{label}</span>
      <span style={s.factValue}>{value || "—"}</span>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={s.field}>
      <span style={s.label}>{label}</span>
      {children}
    </label>
  );
}

const spin = `.spin{animation:spin 1s linear infinite}
              @keyframes spin{to{transform:rotate(360deg)}}`;

const s = {
  root: { background: "#f5f1e8", minHeight: "100vh",
          fontFamily: "Inter, system-ui, sans-serif", color: "#051824" },
  header: { display: "flex", justifyContent: "space-between",
            alignItems: "center", padding: "20px 32px",
            background: "#051824", color: "#f5f1e8" },
  back: { color: "#7be3ff", textDecoration: "none", fontSize: 14,
          display: "inline-flex", alignItems: "center", gap: 6,
          fontWeight: 500 },
  brand: { display: "flex", alignItems: "center", gap: 10,
           color: "#f5f1e8", textDecoration: "none" },
  brandText: { fontFamily: "Georgia, serif", fontWeight: 800,
               fontSize: 20, letterSpacing: "-0.02em" },
  main: { maxWidth: 1200, margin: "0 auto", padding: "0 24px 80px" },
  heroWrap: { padding: "32px 0" },
  hero: { aspectRatio: "21/9", backgroundSize: "cover",
          backgroundPosition: "center", borderRadius: 16,
          position: "relative",
          background: "linear-gradient(135deg, #1aa3c7 0%, #051824 60%, #082a3d 100%)",
          backgroundBlendMode: "overlay" },
  unclaimed: { position: "absolute", top: 18, left: 18,
               background: "rgba(251,191,36,0.95)", color: "#0f172a",
               padding: "6px 14px", borderRadius: 999, fontSize: 12,
               fontWeight: 700, letterSpacing: "0.04em",
               textTransform: "uppercase" },
  claimed: { position: "absolute", top: 18, left: 18,
             background: "rgba(34,197,94,0.95)", color: "white",
             padding: "6px 14px", borderRadius: 999, fontSize: 12,
             fontWeight: 700, letterSpacing: "0.04em",
             textTransform: "uppercase" },
  featured: { position: "absolute", top: 18, right: 18,
              background: "rgba(15,23,42,0.92)", color: "#fbbf24",
              padding: "6px 14px", borderRadius: 999, fontSize: 12,
              fontWeight: 700 },
  content: { display: "grid", gridTemplateColumns: "1fr 380px",
             gap: 48, alignItems: "start" },
  left: {},
  title: { fontFamily: "Georgia, serif", fontSize: 40, fontWeight: 800,
           letterSpacing: "-0.025em", margin: "0 0 8px" },
  region: { display: "flex", alignItems: "center", gap: 8,
            color: "#475569", fontSize: 14, marginBottom: 24 },
  dot: { color: "#cbd5e1" },
  factGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14,
              padding: 20, background: "white", borderRadius: 12,
              border: "1px solid #e0d7c4", marginBottom: 28 },
  fact: { display: "flex", flexDirection: "column", gap: 2 },
  factLabel: { fontSize: 10, letterSpacing: "0.1em",
               textTransform: "uppercase", color: "#64748b",
               fontWeight: 700 },
  factValue: { fontSize: 14, color: "#0f172a", fontWeight: 500 },
  section: { marginBottom: 28 },
  h3: { fontSize: 18, fontWeight: 800, margin: "0 0 8px" },
  body: { color: "#1f2937", lineHeight: 1.65, fontSize: 14, margin: 0 },
  specialty: { display: "inline-flex", alignItems: "center", gap: 6,
               marginTop: 12, padding: "5px 12px", background: "#e0f2fe",
               color: "#075985", borderRadius: 999, fontSize: 12,
               fontWeight: 600 },
  notice: { background: "#fef3c7", border: "1px solid #fde68a",
            borderRadius: 12, padding: 18, fontSize: 13,
            color: "#92400e", lineHeight: 1.55, marginTop: 28 },
  formSide: { position: "sticky", top: 32 },
  form: { background: "white", border: "1px solid #e0d7c4",
          borderRadius: 16, padding: 24, display: "flex",
          flexDirection: "column", gap: 4,
          boxShadow: "0 12px 40px rgba(0,0,0,0.06)" },
  price: { fontSize: 22, fontWeight: 800, marginBottom: 4,
           letterSpacing: "-0.01em" },
  priceSub: { color: "#64748b", fontSize: 13, fontWeight: 400 },
  formTitle: { fontSize: 18, fontWeight: 800, margin: "8px 0 4px" },
  field: { display: "flex", flexDirection: "column", gap: 4,
           marginTop: 12 },
  label: { fontSize: 11, fontWeight: 700, color: "#64748b",
           textTransform: "uppercase", letterSpacing: "0.06em" },
  input: { padding: "10px 12px", border: "1px solid #e0d7c4",
           borderRadius: 8, fontSize: 14, outline: "none",
           fontFamily: "inherit" },
  row2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 },
  submitBtn: { marginTop: 16, padding: "14px 18px", background: "#051824",
               color: "white", border: "none", borderRadius: 10,
               fontWeight: 700, fontSize: 15, cursor: "pointer",
               display: "inline-flex", alignItems: "center",
               justifyContent: "center", gap: 8 },
  subtle: { fontSize: 11, color: "#94a3b8", margin: "10px 0 0",
            textAlign: "center", lineHeight: 1.5 },
  errBody: { background: "#fee2e2", color: "#991b1b", padding: "10px 12px",
             borderRadius: 8, fontSize: 13, marginTop: 12 },
  doneCard: { background: "white", border: "1px solid #e0d7c4",
              borderRadius: 16, padding: 32, textAlign: "center",
              boxShadow: "0 12px 40px rgba(0,0,0,0.06)" },
  doneBadge: { width: 64, height: 64, borderRadius: "50%",
               background: "#dcfce7", color: "#16a34a",
               display: "inline-flex", alignItems: "center",
               justifyContent: "center", marginBottom: 16 },
  doneTitle: { fontSize: 24, fontWeight: 800, margin: "0 0 8px",
               letterSpacing: "-0.02em" },
  loading: { display: "flex", justifyContent: "center", padding: 80,
             color: "#475569", gap: 12, alignItems: "center" },
  error: { padding: 80, textAlign: "center", color: "#991b1b" },
};
